import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react'
import _ from 'lodash'
import { connect } from 'react-redux'
import {
  PageLayout,
  coreHooks,
  useApplicationNotifications,
} from '@adc/ui-morpheus'
import { NOTIFICATION_ROLES, Search } from '@adc/ui-core'
import type { PaginationState } from '@adc/ui-core'
import type { LookupNotification } from '@adc/sdk/types'
// @ts-ignore no types
import DataTable, { utils as dataTableUtils } from '@adc/datatable'
import classnames from 'classnames/bind'
import logger from '@adc/logger'
import { ROOT_CRUMB } from '../../constants/routing'
import { COMMUNICATIONS } from '../../constants/tableTypes'
import { fetchTable } from '../../actions/communications'
import { columnSelector } from '../../reducers/communications/columnDefinitions'
import { apiToUiDataRowsTranslation } from '../../translations/communications'
import { fetchNotifications as fetchNotificationsApi } from '../../providers/communications'
import styles from './styles.scss'
import CommunicationDetailModal from './communicationDetailModal'

const cx = classnames.bind(styles)

type CommunicationsPageProps = {
  config: object
  columns: object[]
  fetchDataRows: (fetchOptions: unknown) => unknown
  isLoading: boolean
  errorInfo?: object
  rowData: object[]
  notifications: LookupNotification[]
  totalCount?: number
  totalPages?: number
}

const DEFAULT_PAGE_SIZE = 200

const initialDownloadPaginationState = {
  offset: 0,
  pageSize: DEFAULT_PAGE_SIZE / 2,
  totalRetrieved: 0,
  totalCount: 0,
  totalPages: 0,
  currentPage: 1,
}

const CommunicationsPage = (props: CommunicationsPageProps) => {
  const isMounted = coreHooks.useIsMounted()

  const notifications = useApplicationNotifications()

  const [searchTerm, setSearchTerm] = useState<string>('')

  const onLocalSearchChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, data: { value?: string }) => {
      const searchTerm = data.value || ''
      setSearchTerm(searchTerm)
    },
    []
  )

  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: props.totalCount,
    totalPages: props.totalPages,
  })

  /**
   * Download state
   */
  const [downloadPaginationState, setDownloadPaginationState] = useState(
    initialDownloadPaginationState
  )
  const [downloadBatchRows, setDownloadBatchRows] = useState<object[]>([])
  const [downloadBatchSuccess, setDownloadBatchSuccess] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadAbort, setDownloadAbort] = useState(false)
  const downloadAbortRef = useRef(downloadAbort)
  downloadAbortRef.current = downloadAbort // for access in async functions
  const [currentRow, setCurrentRow] = useState<LookupNotification | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const fetchNotifications = useCallback(() => {
    return props.fetchDataRows(paginationState)
  }, [props.fetchDataRows, paginationState])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications, paginationState, paginationState])

  const { Paginator } = coreHooks.useOffsetPagination({
    totalCount: props.totalCount || 0,
    pageSize: paginationState.pageSize,
    onPageChange: (paginationState: PaginationState) => {
      setPaginationState(s => ({
        ...s,
        ...paginationState,
      }))
    },
  })

  const hasMorePages = (paginationState.totalPages ?? 0) > 1

  const flattenedData = useMemo(
    () => apiToUiDataRowsTranslation(props.rowData),
    [props.rowData]
  )

  const filteredRows = useMemo(
    () =>
      searchTerm
        ? flattenedData.filter(row => {
            return Object.values(row).some(value =>
              _.toString(value).toLowerCase().includes(searchTerm.toLowerCase())
            )
          })
        : flattenedData,
    [props.rowData, searchTerm]
  )

  const customToolbar = useCallback(
    (props: { renderDataTableActions: () => React.ReactNode }) => (
      <div className={cx('communications-table-toolbar')}>
        {/* @ts-ignore TS2786: 'Search' cannot be used as a JSX component */}
        <Search
          className={cx('local-search')}
          placeholder='Search'
          onSearchChange={onLocalSearchChange}
          showNoResults={false}
        />
        {props.renderDataTableActions()}
      </div>
    ),
    [onLocalSearchChange]
  )

  const customTableFooter = useCallback(
    () => hasMorePages && <div className='u-mt-1'>{Paginator}</div>,
    [Paginator, hasMorePages]
  )

  const resetDownloadState = () => {
    setDownloadPaginationState(initialDownloadPaginationState)
    setDownloadBatchRows([])
    setDownloadBatchSuccess(false)
    setDownloadAbort(false)
    setIsDownloading(false)
  }

  const fetchDownloadData = useCallback(async () => {
    // Prevent concurrent downloads
    if (isDownloading) {
      logger.warn('Download already in progress, skipping new request')
      return
    }

    if (!hasMorePages && props.rowData.length) {
      setDownloadBatchRows(props.rowData)
      setDownloadBatchSuccess(true)
      return
    }

    setIsDownloading(true)

    // Reset state when starting new download
    setDownloadAbort(false)
    setDownloadBatchRows([])
    setDownloadBatchSuccess(false)

    // Recursive function to fetch batches
    const fetchBatch = async (
      currentPaginationState: typeof downloadPaginationState
    ) => {
      // Check abort state before fetching
      if (downloadAbortRef.current) {
        setIsDownloading(false)
        logger.info('Download aborted - stopping batch fetch')
        // Reset download state when aborted
        resetDownloadState()
        return
      }

      try {
        const result = await fetchNotificationsApi({}, currentPaginationState)
        const rows = result.content
        const safeRows = rows.map(row => row || { urn: 'Error' })
        const totalCount = result?.total_count || 0

        setDownloadBatchRows(prevRows => [...prevRows, ...safeRows])

        const newTotalRetrieved =
          currentPaginationState.totalRetrieved + safeRows.length
        const newOffset =
          currentPaginationState.offset + currentPaginationState.pageSize
        const hasMoreData =
          newTotalRetrieved < totalCount && safeRows.length > 0

        // Update state
        const newPaginationState = {
          ...currentPaginationState,
          totalRetrieved: newTotalRetrieved,
          totalCount,
          totalPages: result?.total_pages || 0,
          currentPage: currentPaginationState.currentPage + 1,
          offset: newOffset,
        }

        setDownloadPaginationState(newPaginationState)

        // Check if we're done
        if (!hasMoreData || newTotalRetrieved >= totalCount) {
          setDownloadBatchSuccess(true)
          setIsDownloading(false)
          return
        }

        // Check abort state again after processing
        if (!downloadAbortRef.current) {
          // Continue with next batch using updated pagination state
          await fetchBatch(newPaginationState)
        } else {
          setIsDownloading(false)
          logger.info('Download aborted after processing - cleaning up')
          // Reset download state when aborted
          resetDownloadState()
        }
      } catch (error) {
        setDownloadBatchSuccess(false)
        setDownloadAbort(true)
        logger.error('Error fetching Communications', error)
        // eslint-disable-next-line
        notifications.toast.open({
          role: NOTIFICATION_ROLES.ERROR, // eslint-disable-line
          title: 'Error fetching Communications for download',
        })
        setDownloadBatchRows([])
        setIsDownloading(false)
      }
    }

    // Start the recursive fetching with initial state
    // Use a fresh initial state to avoid stale closure issues}
    await fetchBatch(initialDownloadPaginationState)
  }, [notifications, isDownloading, hasMorePages, props.rowData])

  const exportData = useCallback(
    (columns: Record<string, unknown>[]) => {
      const csvData = dataTableUtils.getCSVExportData(
        columns,
        flattenedData
      ) as string
      return csvData
    },
    [flattenedData]
  )

  const handleRowAction = useCallback(
    (_actionType: string, _rowIndex: number, data: LookupNotification) => {
      setCurrentRow(data)
      setIsModalOpen(true)
    },
    []
  )

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const downloadActions = {
    fetchData: fetchDownloadData,
    reset: resetDownloadState,
    exportData,
    abort: () => {
      setDownloadAbort(true)
      logger.info('Download aborted')
    },
  }

  const tableProps = {
    ...props,
    customToolbar,
    customTableFooter,
    fetchDataRows: fetchNotifications,
    rowData: filteredRows,
    allowExportCSV: true,
    downloadActions,
    downloadData: {
      content: downloadBatchRows,
      totalCount: downloadPaginationState.totalCount,
      totalRetrieved: downloadBatchRows.length,
      isBatchSuccessful: downloadBatchSuccess,
    },
    onRowAction: handleRowAction,
  }

  return (
    <PageLayout title='Communications' crumbs={[ROOT_CRUMB, 'Communications']}>
      <section className='full-height u-pt-2 u-mr-4 u-ml-4'>
        {isMounted && (
          <>
            <DataTable
              {...tableProps}
              entityKey={COMMUNICATIONS}
              entityName='Communications'
            />
            {isModalOpen && (
              <CommunicationDetailModal
                communicationData={currentRow}
                onClose={handleModalClose}
              />
            )}
          </>
        )}
      </section>
    </PageLayout>
  )
}

const getTotalCount = (state: { total_count: number }) => state.total_count
const getTotalPages = (state: { total_pages: number }) => state.total_pages

const mapStateToProps = (store: { [COMMUNICATIONS]: any }) => {
  const { content, isLoading, errorInfo } = store[COMMUNICATIONS].data
  const config = store[COMMUNICATIONS].tableInfo.config

  return {
    config,
    isLoading: !!isLoading,
    columns: columnSelector(store[COMMUNICATIONS].tableInfo),
    totalCount: getTotalCount(store[COMMUNICATIONS].data),
    totalPages: getTotalPages(store[COMMUNICATIONS].data),
    rowData: content,
    errorInfo,
  }
}

const mapDispatchToProps = (dispatch: (arg: unknown) => unknown): unknown => ({
  fetchDataRows: (fetchOptions: unknown) => dispatch(fetchTable(fetchOptions)),
})

export default connect(mapStateToProps, mapDispatchToProps)(CommunicationsPage)
